import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Volume2 } from 'lucide-react';
import { speechService } from '../services/speechService';

const SENSE_LIMIT = 5;
const WORD_LINK_LIMIT = 8;

// IPA plus one listen button per recorded accent (US, UK...), or a
// text-to-speech button when the dictionary has no recording
export const Pronunciation = ({ entry, className = '' }) => {
  const [playing, setPlaying] = useState(null);
  const clips = entry.audio.length > 0 ? entry.audio : [{ region: '', url: null }];

  const play = async (clip, key) => {
    if (playing) return;
    setPlaying(key);
    try {
      await speechService.speakWord(entry.word, clip.url, 'en');
    } catch (error) {
      console.error('Speech failed:', error);
    } finally {
      setPlaying(null);
    }
  };

  return (
    <div className={`flex flex-wrap items-center gap-x-4 gap-y-2 ${className}`}>
      {entry.phonetic && (
        <span className="font-serif text-xl text-ink-soft" data-testid="phonetic">{entry.phonetic}</span>
      )}
      <div className="flex flex-wrap gap-2">
        {clips.map((clip, index) => {
          const key = clip.region || `clip-${index}`;
          const label = clip.region ? `${clip.region} pronunciation` : 'Listen';
          return (
            <button
              key={key}
              type="button"
              onClick={() => play(clip, key)}
              disabled={Boolean(playing)}
              className={`inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-[0.95rem] transition-colors disabled:cursor-default ${
                playing === key
                  ? 'border-ink bg-ink text-paper'
                  : 'border-rule text-ink hover:border-ink disabled:opacity-60'
              }`}
              aria-label={`Play ${label.toLowerCase()} of ${entry.word}`}
              data-testid={index === 0 ? 'pronunciation-button' : `phonetic-audio-${index}`}
            >
              {playing === key ? (
                <span className="sound-bars flex h-4 items-end gap-[2px]" aria-hidden="true">
                  <span /><span /><span />
                </span>
              ) : (
                <Volume2 size={16} aria-hidden="true" />
              )}
              {clip.region || 'Listen'}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// "Similar: chance, fluke, accident" written as a sentence, not chips
export const WordLinks = ({ label, words, className = '' }) => {
  if (!words || words.length === 0) return null;
  const shown = words.slice(0, WORD_LINK_LIMIT);

  return (
    <p className={`font-sans text-[0.95rem] text-ink-soft ${className}`}>
      <span className="font-medium text-ink">{label}:</span>{' '}
      {shown.map((word, index) => (
        <React.Fragment key={word}>
          {index > 0 && ', '}
          <Link to={`/define/${encodeURIComponent(word)}`} className="text-link">{word}</Link>
        </React.Fragment>
      ))}
    </p>
  );
};

// One part of speech: the label in the margin, numbered senses beside it
export const SenseGroup = ({ group, limit = SENSE_LIMIT, headingLevel = 2 }) => {
  const [expanded, setExpanded] = useState(false);
  const Heading = `h${headingLevel}`;
  const headingId = `pos-${group.partOfSpeech.replace(/\s+/g, '-')}`;
  const hidden = group.senses.length - limit;
  const senses = expanded || hidden <= 0 ? group.senses : group.senses.slice(0, limit);

  // Group-level synonyms not already listed under a sense
  const senseWords = new Set(group.senses.flatMap((s) => [...s.synonyms, ...s.antonyms]));
  const moreSynonyms = group.synonyms.filter((w) => !senseWords.has(w));
  const moreAntonyms = group.antonyms.filter((w) => !senseWords.has(w));

  return (
    <section
      aria-labelledby={headingId}
      className="grid scroll-mt-24 gap-x-10 gap-y-4 border-t border-rule py-8 md:grid-cols-[8.5rem_minmax(0,1fr)]"
    >
      <Heading id={headingId} className="font-serif text-2xl italic text-ink">
        {group.partOfSpeech}
      </Heading>

      <div className="min-w-0">
        <ol className="space-y-6">
          {senses.map((sense, index) => (
            <li key={sense.definition} className="grid grid-cols-[1.75rem_minmax(0,1fr)] gap-x-2">
              <span aria-hidden="true" className="pt-[0.35rem] font-sans text-sm font-semibold tabular-nums text-ink-soft">
                {index + 1}
              </span>
              <div className="max-w-[42rem] font-serif text-lg leading-[1.7]">
                <p>{sense.definition}</p>
                {sense.example && (
                  <p className="mt-1 italic text-ink-soft">“{sense.example}”</p>
                )}
                <WordLinks label="Similar" words={sense.synonyms} className="mt-2" />
                <WordLinks label="Opposite" words={sense.antonyms} className="mt-1" />
              </div>
            </li>
          ))}
        </ol>

        {hidden > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            className="ml-[2.25rem] mt-6 rounded-sm text-[0.95rem] font-medium text-leaf underline decoration-1 underline-offset-4 hover:decoration-2"
            aria-expanded={expanded}
          >
            {expanded ? 'Show fewer meanings' : `Show all ${group.senses.length} meanings`}
          </button>
        )}

        {(moreSynonyms.length > 0 || moreAntonyms.length > 0) && (
          <div className="ml-[2.25rem] mt-6 space-y-1">
            <WordLinks label={`Similar words (${group.partOfSpeech})`} words={moreSynonyms} />
            <WordLinks label={`Opposite words (${group.partOfSpeech})`} words={moreAntonyms} />
          </div>
        )}
      </div>
    </section>
  );
};
